import { useState } from 'react';
import { posApi, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle2, User, AlertTriangle, CreditCard } from 'lucide-react';
import ActivateSubscriptionDialog from '@/components/shared/ActivateSubscriptionDialog';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CustomerProfileView } from '@/components/shared/CustomerProfileView';

const QTY_OPTIONS = [1, 2, 3, 4, 5];

const STATUS_STYLES = {
  ACTIVE: 'bg-coriander/10 text-coriander',
  PENDING: 'bg-amber-100 text-amber-700',
  PAUSED: 'bg-amber-100 text-amber-700',
  COMPLETED: 'bg-muted text-muted-ink',
  EXPIRED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-red-100 text-red-700',
};

// Shared "brief profile + deduct" panel used by both the POS QR scanner and manual search —
// they both resolve to the same {customer, subscription} shape before handing off here.
export default function CustomerActionPanel({ customer, subscription, onReset }) {
  const [quantity, setQuantity] = useState(1);
  const [customQty, setCustomQty] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState(null);
  const [showFullProfile, setShowFullProfile] = useState(false);
  const [fullProfile, setFullProfile] = useState(null);
  const [showActivate, setShowActivate] = useState(false);

  const finalQuantity = () => (quantity === 'custom' ? parseInt(customQty) || 0 : quantity);

  const confirmDeduct = async () => {
    if (!subscription || confirming) return;
    setConfirming(true);
    try {
      const { data } = await posApi.post('/redemptions', {
        subscriptionId: subscription.id,
        quantity: finalQuantity(),
        method: 'QR',
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setConfirming(false);
    }
  };

  const loadFullProfile = async () => {
    setShowFullProfile(true);
    if (fullProfile) return;
    try {
      const { data } = await posApi.get(`/customers/${customer.id}`);
      setFullProfile(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (result) {
    return (
      <div className="bg-white rounded-2xl p-6 text-center space-y-3" data-testid="pos-deduction-success">
        <CheckCircle2 size={40} className="text-coriander mx-auto" />
        <p className="font-heading text-xl font-semibold text-charcoal">Deduction Successful</p>
        <p className="text-muted-ink">{result.redemption.quantity} Tiffin{result.redemption.quantity > 1 ? 's' : ''} Deducted</p>
        <p className="font-heading text-3xl font-700 text-charcoal" data-testid="pos-new-balance">
          Remaining: {result.subscription.remainingCredits}
        </p>
        {result.subscription.remainingCredits <= 2 && (
          <p
            className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center justify-center gap-2"
            data-testid="pos-low-balance-nudge"
          >
            <AlertTriangle size={16} className="shrink-0" /> Remind {customer.name.split(' ')[0]} to renew — only{' '}
            {result.subscription.remainingCredits} tiffin{result.subscription.remainingCredits === 1 ? '' : 's'} left!
          </p>
        )}
        <button
          data-testid="pos-scan-next-btn"
          onClick={onReset}
          className="w-full h-14 rounded-xl bg-saffron hover:bg-saffron-hover text-white font-semibold text-lg transition-colors duration-150"
        >
          Scan Next Customer
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6 space-y-5" data-testid="pos-customer-panel">
      <div>
        <p className="font-heading text-xl font-semibold text-charcoal" data-testid="pos-customer-name">{customer.name}</p>
        <p className="text-sm text-muted-ink">{customer.phone}{subscription ? ` · ${subscription.plan?.name || subscription.planNameSnapshot}` : ''}</p>
      </div>

      {subscription ? (
        <>
          {subscription.status === 'ACTIVE' && subscription.remainingCredits <= 2 && (
            <p
              className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2"
              data-testid="pos-low-balance-warning"
            >
              <AlertTriangle size={16} className="shrink-0" /> Low balance — remind this customer to renew soon.
            </p>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-bone rounded-xl p-3">
              <p className="text-xs text-muted-ink">Remaining Tiffins</p>
              <p className="font-heading text-2xl font-700 text-charcoal" data-testid="pos-remaining-balance">{subscription.remainingCredits}</p>
            </div>
            <div className="bg-bone rounded-xl p-3">
              <p className="text-xs text-muted-ink">Status</p>
              <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[subscription.status] || 'bg-muted'}`}>
                {subscription.status}
              </span>
            </div>
          </div>

          {subscription.status === 'ACTIVE' ? (
            <>
              <div>
                <p className="text-sm font-medium text-charcoal mb-2">Tiffins being collected</p>
                <div className="grid grid-cols-3 gap-2">
                  {QTY_OPTIONS.map((n) => (
                    <button
                      key={n}
                      data-testid={`pos-qty-${n}`}
                      onClick={() => setQuantity(n)}
                      className={`h-14 rounded-xl border-2 text-xl font-semibold transition-colors duration-150 ${
                        quantity === n ? 'bg-saffron text-white border-saffron' : 'border-border/40 text-charcoal'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                  <button
                    data-testid="pos-qty-more"
                    onClick={() => setQuantity('custom')}
                    className={`h-14 rounded-xl border-2 text-sm font-semibold transition-colors duration-150 ${
                      quantity === 'custom' ? 'bg-saffron text-white border-saffron' : 'border-border/40 text-charcoal'
                    }`}
                  >
                    More
                  </button>
                </div>
                {quantity === 'custom' && (
                  <input
                    type="number"
                    min={1}
                    data-testid="pos-qty-custom-input"
                    value={customQty}
                    onChange={(e) => setCustomQty(e.target.value)}
                    className="mt-2 w-full h-14 rounded-xl border border-border/40 px-4 text-xl text-center"
                    placeholder="Quantity"
                  />
                )}
              </div>

              <div className="bg-bone rounded-xl p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-ink">Current Balance</span><span>{subscription.remainingCredits}</span></div>
                <div className="flex justify-between"><span className="text-muted-ink">Deducting</span><span>{finalQuantity() || 0}</span></div>
                <div className="flex justify-between font-semibold"><span>New Balance</span><span data-testid="pos-projected-balance">{subscription.remainingCredits - (finalQuantity() || 0)}</span></div>
              </div>

              <button
                data-testid="pos-confirm-deduct-btn"
                disabled={confirming || finalQuantity() < 1 || finalQuantity() > subscription.remainingCredits}
                onClick={confirmDeduct}
                className="w-full h-16 rounded-xl bg-saffron hover:bg-saffron-hover disabled:opacity-50 text-white font-semibold text-lg transition-colors duration-150"
              >
                {confirming ? 'Deducting…' : 'Confirm Deduction'}
              </button>
            </>
          ) : subscription.status === 'PENDING' ? (
            <div className="space-y-3">
              <p className="text-sm text-amber-700 bg-amber-50 rounded-xl p-3" data-testid="pos-subscription-pending">
                Payment pending — no tiffins can be deducted until this subscription is activated.
              </p>
              <button
                data-testid="pos-proceed-to-payment-btn"
                onClick={() => setShowActivate(true)}
                className="w-full h-16 rounded-xl bg-saffron hover:bg-saffron-hover text-white font-semibold text-lg flex items-center justify-center gap-2 transition-colors duration-150"
              >
                <CreditCard size={20} /> Proceed to Payment
              </button>
            </div>
          ) : (
            <p className="text-sm text-amber-700 bg-amber-50 rounded-xl p-3" data-testid="pos-subscription-not-active">
              This subscription is {subscription.status.toLowerCase()} — no tiffins can be deducted right now.
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-amber-700 bg-amber-50 rounded-xl p-3" data-testid="pos-no-subscription">
          This customer has no subscription yet.
        </p>
      )}

      <button
        data-testid="pos-view-profile-btn"
        onClick={loadFullProfile}
        className="w-full h-11 rounded-xl border border-border/40 text-charcoal flex items-center justify-center gap-2 text-sm"
      >
        <User size={15} /> View Full Profile
      </button>

      {showFullProfile && fullProfile && (
        <Dialog open={showFullProfile} onOpenChange={setShowFullProfile}>
          <DialogContent data-testid="pos-full-profile" className="max-w-3xl max-h-[88vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{fullProfile.customer.name}</DialogTitle>
              <DialogDescription>
                {fullProfile.customer.customerCode} · {fullProfile.customer.phone} · {fullProfile.customer.email}
              </DialogDescription>
            </DialogHeader>
            <CustomerProfileView
              customer={fullProfile.customer}
              redemptions={fullProfile.redemptions}
              payments={fullProfile.payments}
            />
          </DialogContent>
        </Dialog>
      )}

      <button data-testid="pos-cancel-btn" onClick={onReset} className="w-full h-10 text-sm text-muted-ink">
        Cancel
      </button>

      {showActivate && subscription && (
        <ActivateSubscriptionDialog
          api={posApi}
          subscriptionId={subscription.id}
          onClose={() => setShowActivate(false)}
          onDone={() => {
            setShowActivate(false);
            toast.success(`${customer.name.split(' ')[0]}'s subscription is now active`);
            onReset();
          }}
        />
      )}
    </div>
  );
}
